<script setup lang="ts">
import { tpgIconChevronLeft, tpgIconChevronRight } from '@tpg/grammar-web-icons';
import { computed } from 'vue';

import GrammarQuaternaryButton from './GrammarQuaternaryButton.vue';
import GrammarText from './GrammarText.vue';

export interface GrammarPaginatorProps {
  /**
   * Index of the first record
   */
  first?: number;
  /**
   * Number of rows to display per page
   */
  limit?: number;
  /**
   * Total number of records
   */
  total?: number;
}

export interface PageChangeEvent {
  first: number;
  rows: number;
  page: number;
  pageCount: number;
}

const props = withDefaults(defineProps<GrammarPaginatorProps>(), {
  first: 0,
  limit: 10,
  total: 0,
});

const emit = defineEmits<{
  pageChange: [event: PageChangeEvent];
}>();

const currentPage = computed(() => {
  return Math.floor(props.first / props.limit) + 1;
});

const totalPages = computed(() => {
  return Math.ceil(props.total / props.limit);
});

const isFirstPage = computed(() => {
  return currentPage.value === 1;
});

const isLastPage = computed(() => {
  return currentPage.value === totalPages.value || totalPages.value === 0;
});

const displayText = computed(() => {
  if (props.total === 0) {
    return '0 of 0';
  }

  const start = props.first + 1;
  const end = Math.min(props.first + props.limit, props.total);

  return `${start}-${end} of ${props.total}`;
});

function changePage(newPage: number) {
  const newFirst = (newPage - 1) * props.limit;

  emit('pageChange', {
    first: newFirst,
    rows: props.limit,
    page: newPage - 1,
    pageCount: totalPages.value,
  });
}

function goToPreviousPage() {
  if (!isFirstPage.value) {
    changePage(currentPage.value - 1);
  }
}

function goToNextPage() {
  if (!isLastPage.value) {
    changePage(currentPage.value + 1);
  }
}
</script>

<template>
  <div class="flex items-center justify-between gap-4 px-4 py-2">
    <GrammarQuaternaryButton
      :icon-template="tpgIconChevronLeft"
      :disabled="isFirstPage"
      aria-label="Previous page"
      @click="goToPreviousPage"
    />

    <GrammarText :text="displayText" />

    <GrammarQuaternaryButton
      :icon-template="tpgIconChevronRight"
      :disabled="isLastPage"
      aria-label="Next page"
      @click="goToNextPage"
    />
  </div>
</template>
